/**
 * Progressive US phone mask for text inputs as the user types.
 *
 * Reformats from the raw digits each keystroke (so it survives paste + backspace):
 *   ''           -> ''
 *   '5'          -> '(5'
 *   '512'        -> '(512'
 *   '5125'       -> '(512) 5'
 *   '512555'     -> '(512) 555'
 *   '5125550148' -> '(512) 555-0148'
 * Non-digits are stripped; anything past 10 digits is dropped.
 */
export function formatUsPhone(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * US phone formatter that preserves an explicit +1 country code. Used by
 * account surfaces where the canonical value is commonly stored as E.164.
 */
export function formatUsPhoneWithCountry(input: string): string {
  const digits = input.replace(/\D/g, '');
  const hasCountryCode = input.trimStart().startsWith('+1') || (digits.length > 10 && digits.startsWith('1'));
  const national = hasCountryCode ? digits.slice(1, 11) : digits.slice(0, 10);
  const formatted = formatUsPhone(national);
  return hasCountryCode ? `+1${formatted ? ` ${formatted}` : ''}` : formatted;
}

/** True when a text field looks like a phone field (by id or label). */
const PHONE_RE = /\b(phone|mobile|cell|telephone)\b/i;
export function isPhoneField(fieldId: string, label: string): boolean {
  return PHONE_RE.test(fieldId) || PHONE_RE.test(label);
}
